<template>
    <v-card width="100%" class="mb-3" elevation="1">
        <v-card-title class="text-subtitle-1 font-weight-bold">Penalties</v-card-title>
        <v-row v-if="!readOnly" class="px-4" align="center">
            <v-col cols="6" lg="2">
                <v-switch
                    v-model="newPenalty.permanent"
                    class="mt-0 pt-0"
                    hide-details
                    inset
                    :label="newPenalty.permanent ? 'Permanent' : 'Matches'"
                />
            </v-col>
            <v-col cols="6" lg="2">
                <v-select
                    v-model="newPenalty.matches"
                    :items="matchOptions"
                    :disabled="newPenalty.permanent"
                    label="Matches"
                    hide-details
                />
            </v-col>
            <v-col cols="6" lg="2">
                <date-time-picker
                    :date-label="'Start Date'"
                    :date="startDate"
                    hide-details
                    @setDate="setStartDate"
                />
            </v-col>
            <v-col cols="6" lg="2">
                <v-autocomplete
                    v-model="newPenalty.seasonId"
                    :items="seasons"
                    :item-text="(item) => `${item.year} - ${item.type}`"
                    item-value="id"
                    menu-props="auto"
                    label="Season"
                    hide-details
                />
            </v-col>
            <v-col cols="8" lg="3">
                <v-text-field
                    v-model="newPenalty.reason"
                    label="Reason"
                    hide-details
                    clearable
                />
            </v-col>
            <v-col cols="4" lg="1">
                <v-btn
                    :disabled="!canAdd"
                    :loading="loading"
                    class="white--text"
                    color="black"
                    @click="addPenalty"
                >
                    Add
                </v-btn>
            </v-col>
        </v-row>
        <v-simple-table v-if="penalties.length" class="px-4 py-5">
            <thead>
                <tr>
                    <th>Ban</th>
                    <th>Start Date</th>
                    <th>Season</th>
                    <th>Reason</th>
                    <th>Added</th>
                    <th v-if="!readOnly" />
                </tr>
            </thead>
            <tbody>
                <tr v-for="penalty in penalties" :key="penalty.id">
                    <td>
                        <v-chip small :color="penalty.permanent ? 'red' : 'orange'" dark>
                            {{ penalty.permanent ? 'Permanent' : `${penalty.matches} match${penalty.matches > 1 ? 'es' : ''}` }}
                        </v-chip>
                    </td>
                    <td>{{ penalty.startDate }}</td>
                    <td>{{ penalty.season ? `${penalty.season.year} - ${penalty.season.type}` : '' }}</td>
                    <td>{{ penalty.reason }}</td>
                    <td>{{ penalty.createdAt }}</td>
                    <td v-if="!readOnly">
                        <delete-button :type="false" :color="'red'" @delete-entry="deletePenalty(penalty.id)" />
                    </td>
                </tr>
            </tbody>
        </v-simple-table>
        <v-card-text v-else>No penalties</v-card-text>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-card>
</template>

<script>
    import playerService from '@/services/player'
    import seasonService from '@/services/season'
    import Alert from '@/components/Alert'
    import DateTimePicker from '@/components/DateTimePicker'
    import DeleteButton from '@/components/DeleteButton'
    import momentTimezone from 'moment-timezone'

    const emptyPenalty = () => ({
        permanent: false,
        matches: null,
        seasonId: null,
        reason: null
    })

    export default {
        name: 'PlayerPenalties',
        components: { Alert, DateTimePicker, DeleteButton },
        props: {
            playerId: {
                type: [Number, String],
                required: true
            },
            readOnly: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            penalties: [],
            seasons: [],
            newPenalty: emptyPenalty(),
            startDate: null,
            matchOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            loading: false,
            showAlert: false,
            message: null,
            type: null
        }),
        computed: {
            canAdd() {
                return (this.newPenalty.permanent || !!this.newPenalty.matches) &&
                    !!this.startDate && !!this.newPenalty.seasonId
            }
        },
        watch: {
            playerId() {
                this.fetchPenalties()
            }
        },
        created() {
            this.fetchPenalties()
            this.fetchAllSeasons()
        },
        methods: {
            fetchPenalties() {
                playerService.getPlayer(this.playerId).then(resp => {
                    this.penalties = resp.data.penalties || []
                })
            },
            fetchAllSeasons() {
                seasonService.getSeasons().then(resp => {
                    this.seasons = resp.data.content
                })
            },
            setStartDate(date) {
                this.startDate = date
            },
            addPenalty() {
                this.loading = true
                const penalty = {
                    ...this.newPenalty,
                    matches: this.newPenalty.permanent ? null : this.newPenalty.matches,
                    startDate: momentTimezone(this.startDate).format('DD/MM/YYYY')
                }
                playerService.addPenalties(this.playerId, [penalty]).then(() => {
                    this.loading = false
                    this.newPenalty = emptyPenalty()
                    this.startDate = null
                    this.fetchPenalties()
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            deletePenalty(penaltyId) {
                playerService.deletePenalty(this.playerId, penaltyId).then(() => {
                    this.fetchPenalties()
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            }
        }
    }
</script>
